import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { shutdown } from '@intercom/messenger-js-sdk';
import { OAuthService } from 'angular-oauth2-oidc';
import { from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AccountService } from '../services/account.service';
import { StorageKeys, StorageService } from '../services/storage.service';
import { authConfig } from './auth.config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private oauthService: OAuthService,
    private storageService: StorageService,
    private account: AccountService,
  ) {
    this.oauthService.configure(authConfig);
    this.oauthService.setStorage(localStorage);
  }

  login(email: string, token?: string): void {
    this.oauthService.configure(authConfig);
    const encodedEmail = encodeURIComponent(email);
    this.oauthService.initCodeFlow('', { email: encodedEmail, token, login_hint: email });
  }

  resetPassword(token?: string, invite?: boolean): void {
    this.oauthService.configure({ ...authConfig, loginUrl: environment.apiDomain + '/identity/reset-password' });
    this.oauthService.initCodeFlow('', { token, invite });
  }

  register(email: string, firstName: string, lastName: string): void {
    const registerUrl = new URL(environment.apiDomain + '/identity/register');
    registerUrl.searchParams.set('email', email);
    registerUrl.searchParams.set('first_name', firstName);
    registerUrl.searchParams.set('last_name', lastName);
    window.location.assign(registerUrl.toString());
  }

  logout(errorMessage?: string): void {
    this.oauthService.logOut();
    this.storageService.clear();
    this.account.clearProfile();
    shutdown();

    if (errorMessage) {
      sessionStorage.setItem(StorageKeys.LOGOUT_ERROR, errorMessage);
    }
  }

  tryLoginCodeFlow(): Promise<unknown> {
    return this.oauthService.tryLoginCodeFlow({ preventClearHashAfterLogin: true });
  }

  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  getUserClaims(): Record<string, any> {
    return this.oauthService.getIdentityClaims() || {};
  }

  getGrantedScopes(): object {
    return this.oauthService.getGrantedScopes();
  }

  hasRefreshToken(): boolean {
    return !!this.oauthService.getRefreshToken();
  }

  refresh(): Observable<boolean> {
    return from(this.oauthService.refreshToken()).pipe(
      switchMap(() => {
        const enterpriseId = this.storageService.getItem(StorageKeys.TOKEN_EXCHANGE_ENTERPRISE_ID);
        return enterpriseId ? from(this.exchangeTokenForEnterprise(enterpriseId)) : of(undefined);
      }),
      map(() => true),
    );
  }

  async exchangeTokenForEnterprise(enterpriseId: string): Promise<void> {
    const tokenRequestBody = new HttpParams()
      .set('grant_type', 'urn:ietf:params:oauth:grant-type:token-exchange')
      .set('subject_token', this.oauthService.getAccessToken())
      .set('subject_token_type', 'urn:ietf:params:oauth:token-type:access_token')
      .set('resource', `urn:ergoplus:enterprise:${enterpriseId}`)
      .set('client_id', environment.oauthClientId);

    const response = await new Promise<TokenExchangeResponse>((resolve, reject) => {
      this.http
        .post<TokenExchangeResponse>(environment.apiDomain + '/identity/oauth2/token', tokenRequestBody.toString(), {
          headers: new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
          }),
        })
        .subscribe(resolve, reject);
    });

    this.applyTokenExchangeResponse(response, enterpriseId);
  }

  private applyTokenExchangeResponse(response: TokenExchangeResponse, enterpriseId: string): void {
    const now = Date.now();
    const expiresInMs = Math.max(response.expires_in || 0, 0) * 1000;

    this.storageService.setItem(StorageKeys.ACCESS_TOKEN, response.access_token);
    this.storageService.setItem(StorageKeys.TOKEN_TYPE, response.token_type || 'Bearer');
    this.storageService.setItem(StorageKeys.ACCESS_TOKEN_STORED_AT, String(now));
    this.storageService.setItem(StorageKeys.EXPIRES_AT, String(now + expiresInMs));

    if (response.scope) {
      this.storageService.setItem(StorageKeys.GRANTED_SCOPES, JSON.stringify(response.scope.split(' ')));
    }

    this.storageService.setItem(StorageKeys.TOKEN_EXCHANGE_ENTERPRISE_ID, enterpriseId);
  }
}

interface TokenExchangeResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  issued_token_type: string;
}
